import React, { FC, useEffect, useState } from 'react';
import { TextDefault } from '../../Text';
import { Flexbox } from '../../Layout';
import styled from 'styled-components';
import { Icon } from '../../Icon';
import { Colors } from '../../../../styles/colors';
import { Attachment } from '../../../../interfaces/Attachment';
import { downloadDocument } from '../../../../utils/Attachments';
import { ConfirmationPopup, useShowConfirmationPopup } from '../../ConfirmationPopup';
import { DefaultInput } from '../../forms/DefaultInput';
import { useApiDispatchQueue } from '../../../../state/ApiDispatchState';
import { useShowAlertPopup } from '../../../../state/hooks/AlertHooks';
import { getAuthHeader } from '../../../store/AuthStore';

const DocumentDownloadWrapper = styled(Flexbox)`
  margin-bottom: 1.5rem;

  & > * {
    &:first-child {
      margin-right: 2rem;
    }
  }
`;

const Link = styled(TextDefault)`
  padding: 0 .5rem;
  margin: .5rem 0;
  cursor: pointer;
  
  @media (pointer: fine) {
    &:hover {
      color: ${Colors.tertiary};
    }
  }

  & + & {
    border-left: 1px solid ${Colors.grayDark};
  }
`;

interface Props {
  emailReceiver?: string;
  operationId?: string;
  attachment: Attachment;
  onDocumentAction?: (type: 'view' | 'download' | 'send', attachment: Attachment, receiver?: string) => void;
}

export const QuestionnaireDocumentDownload: FC<Props> = ({ attachment, operationId, emailReceiver, ...props }) => {
  const showNotificationBanner = useShowAlertPopup();
  const apiDispatch = useApiDispatchQueue();
  const [showSendDialog, setShowSendDialog] = useState<boolean>(false);
  const [receiver, setReceiver] = useState<string>('');

  useEffect(() => {
    if (emailReceiver) {
      setReceiver(emailReceiver);
    }
  }, [emailReceiver]);

  return (
    <>
      {
        showSendDialog &&
        <ConfirmationPopup config={{
          open: showSendDialog,
          closeFn: () => {
            setShowSendDialog(false);
          },
          actionButtonLabel: 'Dokument versenden',
          actionFn: () => {
            if (receiver.length > 3 && receiver.includes('@')) {

              apiDispatch({
                method: 'post',
                route: `/api/operations/${operationId}/send/document`,
                body: { documentId: attachment.id, receiver },
                header: getAuthHeader(),
              });

              setShowSendDialog(false);
              showNotificationBanner({
                error: false,
                renderContent: () => (<>Der Dokumentenversand wurde angestoßen.</>),
              });

              // log activity 'send'
              if (props.onDocumentAction) {
                props.onDocumentAction('send', attachment, receiver);
              }
            } else {
              setShowSendDialog(false);
              showNotificationBanner({
                error: true,
                renderContent: () => (<>Bitte geben Sie eine gültige E-Mail Adresse ein.</>),
              });
            }
          },
          renderer: () => {
            return (
              <DefaultInput
                id={'receiver'}
                label={'Empfänger E-Mail'}
                type={'mail'}
                placeholder={'max@mustermann.de'}
                errorMessage={'Bitte geben Sie eine gültige E-Mail Adresse ein.'}
                required={true}
                value={receiver}
                whiteBackground={true}
                onChange={(event) => {
                  console.log(event.target.value);
                  setReceiver(event.target.value);
                }}
              />
            );
          },
        }} />
      }
      <DocumentDownloadWrapper>
        <Icon icon={'pdf'} size={40} />
        <Link onClick={() => {
          fetch(attachment?.dataUrl)
            .then((res) => res.blob())
            .then((blob) => {
              window.open(URL.createObjectURL(blob).toString());
            })
            .catch((err) => {
              //
            });

          // log activity 'view'
          if (props.onDocumentAction) {
            props.onDocumentAction('view', attachment);
          }
        }}>Ansicht</Link>
        <Link onClick={() => {
          downloadDocument(
            attachment.dataUrl,
            attachment.fileName,
          );

          // log activity 'download'
          if (props.onDocumentAction) {
            props.onDocumentAction('download', attachment);
          }
        }}>Download</Link>
        {
          operationId &&
          <Link onClick={() => {
            setShowSendDialog(true);
          }}>Versenden</Link>
        }
      </DocumentDownloadWrapper>
    </>);
};
